import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Alert, Button, Container, Panel } from '../components/site/ui';
import { IconArrowRight, IconCheckCircle } from '../components/icons';

/**
 * Where Stripe returns a completed subscription checkout. Both audiences land
 * here — the flow either side of it is identical, only the endpoint that
 * applies the payment and the words differ.
 */
const AUDIENCES = {
  merchant: {
    confirmUrl: '/api/stripe/confirm-plan-checkout',
    blurb:
      "Thank you for subscribing to ParcelBuddy. When customers report delivery issues, we'll send you secure links to review and resolve cases.",
    loginTo: '/merchant/login',
    moreTo: '/merchants'
  },
  courier: {
    confirmUrl: '/api/couriers/confirm-plan-checkout',
    blurb:
      'Your Intelligence plan is active. Your dashboard now shows the full picture of what customers are reporting against your network — the retailers those reports cluster around, postcode hotspots and the cases themselves.',
    loginTo: '/courier/login',
    moreTo: '/couriers'
  }
};

const SubscribeSuccessPage = ({ audience = 'merchant' }) => {
  const config = AUDIENCES[audience] || AUDIENCES.merchant;
  const navigate = useNavigate();
  // Stripe redirects here the instant Checkout completes, which can easily
  // be before the `checkout.session.completed` webhook has actually applied
  // the subscription (or, in a local dev environment with no `stripe listen`
  // forwarding running, before it ever does) — confirming the session
  // directly means this page's own merchant doesn't depend on that webhook
  // timing to actually be activated.
  const [confirming, setConfirming] = useState(
    () => new URLSearchParams(window.location.search).get('session_id') != null
  );
  // Whether that confirm came back with a password still to set. Only a
  // brand-new account does; someone who already had a login (a pilot
  // upgrading, say) gets no token and nothing more to do here.
  const [needsPassword, setNeedsPassword] = useState(false);
  // React 18 StrictMode runs effects twice in development. The second call is
  // harmless on the server (it replays), but it comes back without the
  // set-password token, so without this guard the password step would
  // disappear on every dev load.
  const confirmedRef = useRef(false);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    window.history.replaceState(null, '', window.location.pathname);
    if (!sessionId || confirmedRef.current) return;
    confirmedRef.current = true;

    api
      .post(config.confirmUrl, { sessionId })
      .then(({ data }) => {
        // A new account has no password yet, and the same link is sitting in
        // their inbox — but they are right here, so finish the signup now
        // rather than sending them to go and find it.
        if (data?.setPasswordToken) {
          navigate(`/set-password/${data.setPasswordToken}`, { replace: true });
        }
        // No token means this account already has a login — nothing further
        // to do here, so the plain confirmation below stands.
      })
      .catch((err) => {
        console.error('Confirm checkout error:', err);
        // The subscription is paid for either way — the webhook applies it
        // even if this call failed — so point them at the emailed link
        // rather than claiming something went wrong with their payment.
        setNeedsPassword(true);
      })
      .finally(() => setConfirming(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (confirming) {
    return (
      <Container width="sm" className="py-12 sm:py-20">
        <Panel className="text-center">
          <p className="text-sm text-ink-500">Confirming your subscription…</p>
        </Panel>
      </Container>
    );
  }

  return (
    <Container width="sm" className="py-12 sm:py-20">
      <Panel className="text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success-50 text-success-600 ring-1 ring-inset ring-success-100">
          <IconCheckCircle size={32} strokeWidth={1.5} />
        </span>
        <h1 className="mt-6 font-display text-2xl font-800 tracking-tight text-ink-900">
          You&apos;re subscribed!
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-ink-600">
          {config.blurb}
        </p>

        {needsPassword && (
          <Alert tone="info" className="mt-6 text-left">
            Check your email to set your password — the link we&apos;ve just sent takes you into
            your dashboard.
          </Alert>
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button as={Link} to={config.loginTo} size="lg">
            Go to your dashboard
            <IconArrowRight size={17} />
          </Button>
          <Button as={Link} to={config.moreTo} variant="secondary" size="lg">
            How it works
          </Button>
        </div>
      </Panel>
    </Container>
  );
};

export default SubscribeSuccessPage;
